import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { UserPlusIcon, CheckIcon } from '@heroicons/react/24/outline';
import AuthContext from '../../contexts/AuthContext';
import { FollowUser, followUser, unfollowUser } from '../../services/followService';

interface FollowListProps {
  users: FollowUser[];
  loading: boolean;
  emptyMessage: string;
  onFollowToggle?: (userId: string) => Promise<void>;
}

const FollowList: React.FC<FollowListProps> = ({
  users,
  loading,
  emptyMessage,
  onFollowToggle
}) => {
  const { currentUser } = useContext(AuthContext);
  const [followLoading, setFollowLoading] = useState<string | null>(null);

  const handleFollowToggle = async (userId: string) => {
    if (!currentUser || followLoading) return;

    try {
      setFollowLoading(userId);

      if (onFollowToggle) {
        // Si se proporciona una función personalizada, usarla
        await onFollowToggle(userId);
      } else {
        // Si no se proporciona una función personalizada, usar las funciones por defecto
        const userToUpdate = users.find(user => user.uid === userId);

        if (!userToUpdate) {
          console.error('Usuario no encontrado');
          return;
        }

        const isCurrentlyFollowing = userToUpdate.isFollowing;

        // No modificamos directamente el array de usuarios, ya que eso lo debe manejar el componente padre
        // que pasa los usuarios como prop. Esto evita problemas de DOM al actualizar el estado.

        try {
          // Realizar la operación en la base de datos
          if (isCurrentlyFollowing) {
            await unfollowUser(currentUser.uid, userId);
            console.log(`Usuario ${currentUser.uid} dejó de seguir a ${userId}`);
          } else {
            await followUser(
              currentUser.uid,
              userId,
              {
                displayName: currentUser.displayName || 'Usuario',
                photoURL: currentUser.photoURL
              },
              {
                displayName: userToUpdate.displayName,
                photoURL: userToUpdate.photoURL
              }
            );
            console.log(`Usuario ${currentUser.uid} ahora sigue a ${userId}`);
          }
        } catch (dbError) {
          console.error('Error en la base de datos al cambiar estado de seguimiento:', dbError);
          // No podemos revertir el estado aquí porque no lo estamos manejando directamente
        }
      }
    } catch (error) {
      console.error('Error general al cambiar estado de seguimiento:', error);
    } finally {
      setFollowLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-domired-600"></div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200 dark:divide-gray-700">
      {users.map((user) => (
        <div key={user.uid} className="py-4 flex items-center justify-between">
          <Link to={`/profile/${user.uid}`} className="flex items-center group">
            <img
              src={user.photoURL || '/default-avatar.png'}
              alt={user.displayName}
              className="h-12 w-12 rounded-full object-cover border border-gray-200 dark:border-gray-700"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = '/default-avatar.png';
              }}
            />
            <div className="ml-3">
              <div className="font-medium text-gray-900 dark:text-white group-hover:text-domired-600 dark:group-hover:text-domired-400 transition-colors">
                {user.displayName}
              </div>
              {user.bio && (
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                  {user.bio}
                </p>
              )}
            </div>
          </Link>

          {currentUser && currentUser.uid !== user.uid && (
            <button
              onClick={() => handleFollowToggle(user.uid)}
              disabled={followLoading === user.uid}
              className={`ml-2 px-4 py-1.5 rounded-full text-sm font-medium flex items-center ${user.isFollowing
                ? 'bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                : 'bg-domired-600 text-white hover:bg-domired-700'
                } transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-domired-500 disabled:opacity-50`}
            >
              {followLoading === user.uid ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Procesando...
                </>
              ) : user.isFollowing ? (
                <>
                  <CheckIcon className="h-4 w-4 mr-1" />
                  Siguiendo
                </>
              ) : (
                <>
                  <UserPlusIcon className="h-4 w-4 mr-1" />
                  Seguir
                </>
              )}
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default FollowList;
